import React, { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react'
import { Button, Drawer, Spin } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import { useShallow } from 'zustand/shallow'
import { t3cStore } from '@store'
import type { CustomerInfo } from '@types'

const AiCallingContent = lazy(() => import('@pages/ai-calling').then(module => ({
  default: module.AiCallingContent,
})))

const MIN_WIDTH = 720
const WIDTH_STEP = 40
const clampWidth = (width: number, viewportWidth: number) => Math.min(
  viewportWidth,
  Math.max(Math.min(MIN_WIDTH, viewportWidth), width),
)

interface AiCallingDrawerProps {
  customerId: string
  customerInfo?: CustomerInfo
  open: boolean
  onClose: () => void
}

const AiCallingDrawer: React.FC<AiCallingDrawerProps> = ({ customerId, customerInfo, open, onClose }) => {
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth)
  const [width, setWidth] = useState(() => clampWidth(Math.round(window.innerWidth * 0.8), window.innerWidth))
  const { callStatus, sessionId, t3cSdkReady } = t3cStore(useShallow(s => ({
    callStatus: s.callStatus,
    sessionId: s.sessionId,
    t3cSdkReady: s.t3cSdkReady,
  })))
  // 通话进行中：关闭抽屉只隐藏，保留本通数据
  const callActive = callStatus === 'calling' || callStatus === 'talking'
  /**
   * 内容重置标识（内容组件的 key）：只有「开始新的一通电话」才变化。
   *
   * 必须是单一来源：此前用 contentSessionId（渲染期更新）+ callRound（effect 中更新）
   * 两个 state 拼 key，二者分处不同阶段，新通话时 key 会先后变两次，
   * 导致内容组件连续重建两次 —— 其卸载 cleanup 会发 clear 帧并断开 asr-ws，
   * 把刚建立的会话打掉。
   *
   * 取值优先 sessionId（真实通话天然唯一）；尚无 sessionId 时（如 calling 事件早于
   * sessionId 写入的空窗）用轮次兜底，保证新通话一定能触发一次重置。
   */
  const [callKey, setCallKey] = useState(() => sessionId || 'idle')
  const prevCallActiveRef = useRef(callActive)
  /** 内容是否挂载：卸载即清空本通抽屉数据 */
  const [mounted, setMounted] = useState(open)
  const dragRef = useRef<{
    pointerId: number
    startX: number
    startWidth: number
    cursor: string
    userSelect: string
  } | null>(null)

  // 挂断后保留本通内容（sessionId 被清空不重置），新通话拿到新 sessionId 时才释放上一通的
  // 卡片、标注与请求，避免两通电话内容串在一起。
  if (sessionId && sessionId !== callKey) setCallKey(sessionId)

  useEffect(() => {
    // 空闲 → 通话，且此刻尚无 sessionId：用轮次兜底触发一次重置，
    // 待 sessionId 到达后上面的渲染期赋值会再对齐到真实会话 id。
    if (callActive && !prevCallActiveRef.current && !t3cStore.getState().sessionId) {
      setCallKey(`round-${Date.now()}`)
    }
    prevCallActiveRef.current = callActive
  }, [callActive])

  // 内容挂载策略（决定 AI 通话会话的存活边界）：
  //   - 打开抽屉即挂载；
  //   - 通话期间即使关闭抽屉也保持挂载 —— 内容组件卸载会触发其 cleanup，
  //     发出 clear 帧断开 /calling/asr-ws 并丢失已核验的产品名替换，
  //     故通话中必须常驻，关抽屉仅是隐藏（active=false）；
  //   - 电话挂断后再关闭抽屉，才真正卸载并释放本通会话。
  useEffect(() => {
    if (open) setMounted(true)
    else if (!callActive) setMounted(false)
  }, [open, callActive])

  // 抽屉自带通话控制，打开时隐藏 T3C 外呼浮层，避免浮层遮挡界面。
  // 放在抽屉层而非懒加载的内容组件内，保证打开瞬间即生效、不必等分包加载完成。
  // isAiCallingActive 同时告知 callOuted：本抽屉已接管，不要再把浮层显示出来。
  const workbenchHiddenRef = useRef(false)
  useEffect(() => {
    if (!open) return
    t3cStore.getState().setIsAiCallingActive(true)
    return () => {
      workbenchHiddenRef.current = false
      t3cStore.getState().setIsAiCallingActive(false)
      try {
        window.tccc?.UI?.show()
      } catch {
        // SDK 未就绪等异常静默忽略
      }
      t3cStore.getState().setIsWorkbenchVisible(true)
    }
  }, [open])

  useEffect(() => {
    if (!open || !t3cSdkReady || workbenchHiddenRef.current) return
    workbenchHiddenRef.current = true
    try {
      window.tccc?.UI?.hide()
    } catch {
      // SDK 未就绪等异常静默忽略，不影响抽屉主流程
    }
    t3cStore.getState().setIsWorkbenchVisible(false)
  }, [open, t3cSdkReady])

  const stopDragging = useCallback(() => {
    const drag = dragRef.current
    if (!drag) return
    document.body.style.cursor = drag.cursor
    document.body.style.userSelect = drag.userSelect
    dragRef.current = null
  }, [])

  useEffect(() => {
    const handleResize = () => {
      stopDragging()
      setViewportWidth(window.innerWidth)
      setWidth(current => clampWidth(current, window.innerWidth))
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [stopDragging])

  useEffect(() => {
    if (!open) return
    const handlePointerMove = (event: PointerEvent) => {
      const drag = dragRef.current
      if (!drag || event.pointerId !== drag.pointerId) return
      setWidth(clampWidth(drag.startWidth + drag.startX - event.clientX, window.innerWidth))
    }
    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', stopDragging)
    window.addEventListener('pointercancel', stopDragging)
    window.addEventListener('blur', stopDragging)
    return () => {
      stopDragging()
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', stopDragging)
      window.removeEventListener('pointercancel', stopDragging)
      window.removeEventListener('blur', stopDragging)
    }
  }, [open, stopDragging])

  const startDragging = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || !event.isPrimary) return
    event.preventDefault()
    stopDragging()
    event.currentTarget.focus()
    event.currentTarget.setPointerCapture(event.pointerId)
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startWidth: width,
      cursor: document.body.style.cursor,
      userSelect: document.body.style.userSelect,
    }
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const nextWidth = {
      ArrowLeft: width + WIDTH_STEP,
      ArrowRight: width - WIDTH_STEP,
      Home: MIN_WIDTH,
      End: viewportWidth,
    }[event.key]
    if (nextWidth === undefined) return
    event.preventDefault()
    setWidth(clampWidth(nextWidth, viewportWidth))
  }

  return (
    <Drawer
      className="ai-calling-drawer"
      placement="right"
      open={open}
      width={width}
      onClose={onClose}
      closable={false}
      // 点击左侧遮罩即可收起（与关闭图标、Esc 行为一致）：
      // onClose 只收起界面，不挂断电话；通话中内容仍保持挂载，WS 不受影响。
      maskClosable
      destroyOnClose={false}
      push={false}
      bodyStyle={{ padding: 0, overflow: 'auto', minHeight: 0 }}
    >
      <Suspense fallback={
        <div className="ai-calling-drawer__loading">
          <Button
            className="ai-calling-drawer__loading-close"
            type="text"
            icon={<CloseOutlined />}
            aria-label="关闭AI智能通话"
            onClick={onClose}
          />
          <Spin tip="正在加载AI智能通话…" />
        </div>
      }>
        {mounted && (
          <AiCallingContent
            key={callKey}
            customerId={customerId}
            customerInfo={customerInfo}
            embedded
            active={open}
            onClose={onClose}
            resizeHandle={
              <div
                className="ai-calling__split-bar ai-calling-drawer__resize"
                role="separator"
                aria-label="调整AI智能通话抽屉宽度"
                aria-orientation="vertical"
                aria-valuemin={Math.min(MIN_WIDTH, viewportWidth)}
                aria-valuemax={viewportWidth}
                aria-valuenow={width}
                aria-valuetext={`${width}像素`}
                tabIndex={open ? 0 : -1}
                onPointerDown={startDragging}
                onLostPointerCapture={stopDragging}
                onKeyDown={handleKeyDown}
              >
                <span className="ai-calling__split-handle" aria-hidden="true" />
              </div>
            }
          />
        )}
      </Suspense>
    </Drawer>
  )
}

export default AiCallingDrawer
